import { Badge } from "@spz/ui/components/badge";
import { EmptyCell } from "@spz/ui/components/table/cells/EmptyCell";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@spz/ui/components/tooltip";
import { Typography } from "@spz/ui/components/typography";
import { cn } from "../../../lib/utils";
import type { TagCellProps } from "../types";

export const TagCell: React.FC<TagCellProps> = ({
  value,
  variant = "tag",
  tooltipSide = "top",
  className,
  maxVisible = 1,
}) => {
  if (!value) return <EmptyCell />;
  // Convert single string to array if needed
  const tags = Array.isArray(value) ? value : [value];

  // Early return for empty tags
  if (tags.length === 0 || !tags[0]) {
    return null;
  }

  // If we have only one tag or maxVisible is greater than tags length
  if (tags.length <= maxVisible) {
    return (
      <div className={cn("flex gap-2", className)}>
        {tags.map((tag, index) => (
          <Badge
            key={`${tag}-${
              // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
              index
            }`}
            variant={variant}
            className="w-fit truncate capitalize"
          >
            {tag}
          </Badge>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("flex gap-2 items-center", className)}>
      {/* Show first tag(s) up to maxVisible */}
      {tags.slice(0, maxVisible).map((tag, index) => (
        <Badge
          key={`${tag}-${
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            index
          }`}
          variant={variant}
          className="w-fit truncate"
        >
          {tag}
        </Badge>
      ))}

      {/* Show remaining tags in tooltip */}
      <Tooltip>
        <TooltipTrigger>
          <Badge
            variant={variant}
            className="cursor-pointer"
            aria-label={`Show ${tags.length - maxVisible} more tags`}
          >
            +{tags.length - maxVisible}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-[400px]" side={tooltipSide}>
          <Typography variant="body">
            {tags.slice(maxVisible).join(", ")}
          </Typography>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};
